import type * as React from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { statusToneClasses, type StatusMeta, type StatusTone } from '@/components/status-tone';

export type { StatusMeta, StatusTone } from '@/components/status-tone';


const badgeVariant = {
  neutral: 'muted',
  success: 'success',
  warning: 'warning',
  info: 'info',
  danger: 'danger',
} as const;

/**
 * A status pill: the one way to show a record's status (Draft, Needs decision, Done…).
 * Give it a tone and a label, or a `meta` from the page's status map.
 */
export function StatusBadge({
  tone,
  meta,
  dot = false,
  size = 'default',
  shape = 'pill',
  className,
  children,
  ...props
}: Omit<React.ComponentProps<'span'>, 'children'> & {
  tone?: StatusTone;
  meta?: StatusMeta;
  /** sm for inline chips in dense rows and table cells. */
  size?: 'default' | 'sm';
  /** square for a fact among other square chips (a run's summary). */
  shape?: 'pill' | 'square';
  /** Show a coloured dot before the label. */
  dot?: boolean;
  children?: React.ReactNode;
}) {
  const t = meta?.tone ?? tone ?? 'neutral';
  return (
    <Badge variant={badgeVariant[t]} size={size} shape={shape} className={className} {...props}>
      {dot ? <StatusDot tone={t} className={size === 'sm' ? 'size-1.5' : undefined} /> : null}
      {children ?? meta?.label}
    </Badge>
  );
}

/** A small coloured dot for a status, in lists where a full badge is too heavy. */
export function StatusDot({
  tone = 'neutral',
  className,
  ...props
}: React.ComponentProps<'span'> & { tone?: StatusTone }) {
  return (
    <span
      aria-hidden
      className={cn('inline-block size-2 shrink-0 rounded-full', statusToneClasses(tone).dot, className)}
      {...props}
    />
  );
}
